import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Top-level guard. A render-time throw anywhere below this boundary
 * (e.g. a Rules-of-Hooks violation, a malformed API payload) would
 * otherwise unmount the whole React tree and leave a blank white page —
 * exactly the "blank screen after login" failure mode. This catches it
 * and shows a recoverable fallback with a sign-in escape hatch instead.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaces in the browser console / any console-forwarding so a
    // blank-screen report has a stack to chase, not just "it's blank".
    console.error('Dashboard render crashed', error, info.componentStack);
  }

  private signInAgain = (): void => {
    window.location.href =
      'https://millboard.cloudflareaccess.com/cdn-cgi/access/logout?returnTo=' +
      encodeURIComponent(window.location.origin + '/dashboard');
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-page text-fg-default px-6">
        <div className="max-w-[48ch] text-center" role="alert">
          <p className="text-body mb-1">Something went wrong loading the dashboard.</p>
          <p className="text-fg-muted text-small mb-6">
            The page hit an unexpected error and stopped rendering. Reloading or
            signing in again usually clears it.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="border border-rule px-5 py-2 text-eyebrow"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.signInAgain}
              className="bg-fg-default text-mb-spring-wood px-5 py-2 text-eyebrow"
            >
              Sign in again
            </button>
          </div>
          <p className="text-fg-muted text-caption mt-6 mono">{this.state.error.message}</p>
        </div>
      </div>
    );
  }
}
